import { IVehicleProperty, VehicleProvider, VehicleRegisteredInformationSourceType } from '@cbgms/api'
import React from 'react'
import { useQuery } from '@cbgms/base/http/use-query'
import { useFeature } from '@cbgms/base/feature-flags'
import { getVehicleRegisteredInformation } from '../state/actions'
import { useDispatch } from 'react-redux'
import { EmptyUUID } from '@cbgms/base/utils/uuid'
import { Time } from '@carsys/enums/time'

export const useRegisteredInformationTable = (vehicleUUID: string, enabled: boolean, nColumns = 2) => {
  const dispatch = useDispatch()
  const isVisRdwEnabled = useFeature('vis-rdw-provider')
  const visRdwProvider = isVisRdwEnabled ? VehicleProvider.VisRdw : undefined

  const { data: registeredInformation, isLoading } = useQuery(
    ['useRegisteredInformationTable', vehicleUUID, visRdwProvider],
    async () => {
      try {
        if (!vehicleUUID || vehicleUUID === EmptyUUID) return null
        const response = await dispatch(getVehicleRegisteredInformation(vehicleUUID, visRdwProvider))
        return response.Data
      } catch (e: any) {
        if (e?.response?.data?.Error === 'ErrRegistrationNotFound') {
          return null
        }
        throw e
      }
    },
    {
      staleTime: 30 * Time.Second,
      enabled
    }
  )

  const [provider, vehiclePropertiesAsTableRows] = React.useMemo(() => {
    if (!registeredInformation) return []

    let provider = registeredInformation.SourceType as string
    switch (registeredInformation.SourceType) {
      case VehicleRegisteredInformationSourceType.TripleAAA:
        provider = 'AAA Data'
    }

    const tableRows = calculateTableRows(registeredInformation.VehicleProperties, nColumns)

    return [provider, tableRows]
  }, [registeredInformation])

  return React.useMemo(
    () => ({
      isLoading,
      provider,
      vehiclePropertiesAsTableRows,
      registeredInformation: vehiclePropertiesAsTableRows?.length ? registeredInformation : undefined
    }),
    [isLoading, provider, vehiclePropertiesAsTableRows, registeredInformation]
  )
}

// This method returns an array of rows, which contains an array of cells in that row.
//
// IVehicleProperty contains a Key and a Value field. Key is used as the 1st column and Value as the 2nd.
// We want 2 rows, since that makes the best use of the modal width.
// To accomplish this, the array of vehicle properties is split in 2, where the 2nd set of Key/Value columns
// is wrapped after the first half of vehicle properties.
//
// Example: If there are 5 vehicle props, the table would look like this:
//
// | prop1.Key | Prop1.Value | Prop4.Key | Prop4.Value |
// -----------------------------------------------------
// | prop2.Key | Prop2.Value | Prop5.Key | Prop5.Value |
// -----------------------------------------------------
// | prop3.Key | Prop3.Value |           |             |
//
// That's basically what we calculate here, inspired by mongrossiste.fr.
const calculateTableRows = (vehicleProperties: IVehicleProperty[] | undefined, nColumns: number) => {
  const rows: (IVehicleProperty | undefined)[][] = []

  if (!vehicleProperties?.length) {
    return null
  }

  const rowCount = Math.ceil(vehicleProperties.length / nColumns)

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    const row: (IVehicleProperty | undefined)[] = []
    for (let columnIndex = 0; columnIndex < nColumns; columnIndex++) {
      const vehicleProperty = vehicleProperties[rowIndex + columnIndex * rowCount]
      row.push(vehicleProperty) // Each vehicle property has a Key (row cell #1) and a Value (row cell #2).
    }
    rows.push(row)
  }
  return rows
}
